import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api, setAuth, type AuthUser } from '../lib/api';

type LoginData = { token: string; role: 'user' | 'admin'; user: { id: number; username: string } };

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const r = await api<LoginData>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username: username.trim(), password }),
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    const u: AuthUser = { id: r.data.user.id, username: r.data.user.username, role: r.data.role };
    setAuth(r.data.token, u);
    navigate(u.role === 'admin' ? '/admin' : '/app');
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gradient-to-br from-sky-50 to-white px-4 py-10">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10"
      >
        <div className="space-y-2 text-center">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-600 text-2xl font-semibold text-white shadow-sm">
            算
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">珠心算识别</h1>
          <p className="text-base text-slate-500">家长 / 老师 登录使用</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="username" className="text-base font-medium text-slate-700">
            用户名
          </label>
          <input
            id="username"
            type="text"
            autoComplete="username"
            required
            placeholder="老师发给你的账号"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="h-12 w-full rounded-md border border-slate-200 bg-white px-4 text-base text-slate-900 outline-none placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-base font-medium text-slate-700">
            密码
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 w-full rounded-md border border-slate-200 bg-white px-4 text-base text-slate-900 outline-none placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-sky-600 text-base font-semibold text-white shadow-sm transition-colors hover:bg-sky-700 disabled:opacity-50"
        >
          {loading && <Loader2 className="h-5 w-5 animate-spin" />}
          {loading ? '登录中…' : '登录'}
        </button>

        <p className="text-center text-sm text-slate-400">
          没有账号?请联系发给你链接的老师
        </p>
      </form>
    </div>
  );
}
